import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import OwnInnIcon from '@/components/icons/OwnInnIcon';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { useForgotPasswordApi } from '@/features/identity/hooks/useForgotPasswordApi.ts';
import { ErrorKey } from '@/config/apiErrorKeys.ts';
import EToast from '@/components/everridge/EToast.tsx';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';

const FormSchema = z.object({
	email: z.string().email(),
});

const ForgotPasswordForm = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { doGetForgotPasswordLink, isDoGetForgotPasswordLinkPending } =
		useForgotPasswordApi({ onSuccess, onError });
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);

	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
	});

	function onSuccess() {
		navigate('/auth/message/forgotPassword', { replace: true });
		EToast({
			message: t('auth.forgotPasswordForm.toasts.success.message'),
			description: t(
				'auth.forgotPasswordForm.toasts.success.description',
			),
			variant: 'success',
		});
	}

	function onError(error: ErrorKey) {
		console.error(error);
		EToast({
			message: t('auth.forgotPasswordForm.toasts.error.message'),
			description: t('auth.forgotPasswordForm.toasts.error.description'),
			variant: 'error',
		});
	}

	async function onSubmit(data: z.infer<typeof FormSchema>) {
		await doGetForgotPasswordLink(data.email);
	}

	const inputClasses =
		'h-11 p-3.5 border border-popover-100 bg-popover-10 text-xs text-popover-800 placeholder:text-xs placeholder:text-popover-600 active:shadow-input-active error:shadow-input-error';

	return (
		<Form {...form}>
			<div
				className={cn(
					'my-auto flex-1 flex flex-col gap-6 rounded-2.5xl bg-popover-10',
					isMobile ? 'grow' : 'max-w-[20.5rem]',
				)}>
				<div className='flex flex-col items-center gap-4 text-center'>
					<OwnInnIcon className='size-11' />
					<div className='w-[20.5rem] flex flex-col items-center gap-2'>
						<h3 className='text-2xl font-semibold select-none'>
							{t('auth.forgotPasswordForm.text.title')}
						</h3>
						<p className='text-sm text-popover-800 font-medium'>
							{t('auth.forgotPasswordForm.text.subtitle')}
						</p>
					</div>
				</div>
				<form
					className='w-full flex flex-col gap-8'
					onSubmit={form.handleSubmit(onSubmit)}>
					<FormField
						control={form.control}
						name='email'
						render={({ field }) => (
							<FormItem className='flex flex-col gap-1.5'>
								<FormLabel className='text-sm font-medium select-none'>
									{t('elements.form.email.label')}
								</FormLabel>
								<FormControl>
									<Input
										className={inputClasses}
										type='email'
										placeholder={t('your@email.com')}
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<div className='flex flex-col gap-4'>
						<Button type='submit'>
							{t('elements.buttons.resetPassword')}
						</Button>
						<div className='flex justify-center gap-1.5'>
							<span className='text-sm text-popover-800'>
								{t('auth.forgotPasswordForm.form.remember')}
							</span>
							<Button
								onClick={() => navigate('/auth/login')}
								type='button'
								variant='link'
								className='p-0 hover:text-popover-800 active:text-popover-600'>
								{t('elements.buttons.login')}
							</Button>
						</div>
					</div>
				</form>
			</div>
		</Form>
	);
};

export default ForgotPasswordForm;
